import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import {
  AI_CHAT_ACCEPT,
  AI_CHAT_MAX_UPLOAD_BYTES,
  AiChatAttachment,
  AiChatChart,
  AiChatFile,
  downloadAiChatFile,
  streamAiChat,
  uploadAiChatFile,
} from 'src/services/aiChat';
import {
  ConversationMeta,
  deleteConversation,
  getConversationMessages,
  listConversations,
  saveConversation,
  sendMessageFeedback,
} from 'src/services/aiChatHistory';
import Markdown from './Markdown';
import ChartCard from './ChartCard';
import ThinkingIndicator from './ThinkingIndicator';
import {
  ChatEntry,
  clearConversations,
  conversationTitle,
  createConversationId,
  groupConversationsByDay,
  loadConversations,
} from './aiChatHistory.storage';
import styles from './AiChat.module.scss';

// Maps a file extension to a translation-key suffix under tracking.ai_chat.kind_*.
const FILE_KIND_KEYS: Record<string, string> = { PDF: 'document', JSON: 'data', DOCX: 'document', PPTX: 'document' };

const SUGGESTION_KEYS = ['suggestion_1', 'suggestion_5', 'suggestion_2', 'suggestion_3', 'suggestion_4'];

const fileMeta = (filename: string): { ext: string; kindKey: string } => {
  const ext = (filename.split('.').pop() ?? 'file').toUpperCase();
  return { ext, kindKey: FILE_KIND_KEYS[ext] ?? 'table' };
};

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const attachmentIcon = (mimetype: string): 'file' | 'imageUpload' =>
  mimetype.startsWith('image/') ? 'imageUpload' : 'file';

interface AgentStatus {
  label: string;
  step: number;
}

/** A file picked in the composer: uploading, ready to send, or failed. */
interface PendingAttachment {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  status: 'uploading' | 'ready' | 'error';
  attachment?: AiChatAttachment;
  error?: string;
}

const FileCard: React.FC<{ file: AiChatFile }> = ({ file }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const { ext, kindKey } = fileMeta(file.filename);

  let label = t('tracking.ai_chat.download');
  if (busy) label = '…';
  else if (failed) label = t('tracking.ai_chat.retry');

  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await downloadAiChatFile(file);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="self-start w-full max-w-[420px] flex items-center gap-12 rounded-base border-subtle surface-elevated px-12 py-10">
      <div
        className={classNames(
          'flex items-center justify-center w-[40px] h-[40px] shrink-0 rounded-base border-subtle surface-base',
          kindKey === 'table' ? 'status-success-full' : 'text-faded',
        )}>
        <Icon name="file" width={20} height={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="typo-text-14--medium text-full truncate">{file.filename}</div>
        <div className="typo-text-12 text-faded">{`${t(`tracking.ai_chat.kind_${kindKey}`)} · ${ext}`}</div>
      </div>
      <Button title={label} icon="download" tone={ButtonToneProps.SECONDARY} onClick={onDownload} disabled={busy} />
    </div>
  );
};

const AttachmentChip: React.FC<{
  name: string;
  mimetype: string;
  meta?: string;
  error?: boolean;
  onRemove?: () => void;
}> = ({ name, mimetype, meta, error, onRemove }) => {
  const { t } = useTranslation();
  return (
    <span className={classNames(styles.attachment, error && styles.error)} title={name}>
      <Icon name={attachmentIcon(mimetype)} width={14} height={14} />
      <span className={styles.name}>{name}</span>
      {meta && <span className={styles.meta}>{meta}</span>}
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={t('tracking.ai_chat.remove_attachment')}
          title={t('tracking.ai_chat.remove_attachment')}>
          <Icon name="close" width={12} height={12} />
        </button>
      )}
    </span>
  );
};

const FeedbackButtons: React.FC<{ value?: 'up' | 'down'; onChange: (value: 'up' | 'down' | null) => void }> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4">
      {(['up', 'down'] as const).map(kind => (
        <button
          key={kind}
          type="button"
          onClick={() => onChange(value === kind ? null : kind)}
          aria-label={t(`tracking.ai_chat.feedback_${kind}`)}
          title={t(`tracking.ai_chat.feedback_${kind}`)}
          aria-pressed={value === kind}
          className={classNames(styles.feedback, value === kind && styles.active)}>
          {kind === 'up' ? '👍' : '👎'}
        </button>
      ))}
    </div>
  );
};

const AssistantAvatar: React.FC = () => (
  <div className="w-[28px] h-[28px] shrink-0 rounded-base surface-primary-gradient flex items-center justify-center text-white mt-2">
    <Icon name="ai-chat" width={14} height={14} />
  </div>
);

export interface AiChatProps {
  /** Compact mode (overlay): no history sidebar, no chat header. */
  compact?: boolean;
}

const AiChat: React.FC<AiChatProps> = ({ compact = false }) => {
  const { t, i18n } = useTranslation();
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);
  const [historyError, setHistoryError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(() => localStorage.getItem('aiChatHistoryOpen') !== '0');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Refs mirror state synchronously: stream callbacks and the post-stream persist
  // run between renders, so an effect-synced ref would lag and drop the reply.
  const messagesRef = useRef<ChatEntry[]>([]);
  const activeIdRef = useRef<string | null>(null);

  const commitMessages = (next: ChatEntry[]) => {
    messagesRef.current = next;
    setMessages(next);
  };

  const commitActiveId = (id: string | null) => {
    activeIdRef.current = id;
    setActiveId(id);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let metas = await listConversations();
        // One-time migration of conversations kept in this browser before the server-side history existed.
        const local = loadConversations();
        if (metas.length === 0 && local.length > 0) {
          await Promise.all(
            local.slice(0, 30).map(c => saveConversation(c.id, c.title, c.messages).catch(() => undefined)),
          );
          clearConversations();
          metas = await listConversations();
        }
        if (!cancelled) setConversations(metas);
      } catch {
        if (!cancelled) setHistoryError(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [draft]);

  const groups = useMemo(() => groupConversationsByDay(conversations), [conversations]);

  const updateLast = (patch: (last: ChatEntry) => ChatEntry) => {
    const prev = messagesRef.current;
    const last = prev[prev.length - 1];
    commitMessages([...prev.slice(0, -1), patch(last)]);
  };

  const appendToLast = (delta: string) => updateLast(last => ({ ...last, content: last.content + delta }));

  const attachFileToLast = (file: AiChatFile) =>
    updateLast(last => ({ ...last, files: [...(last.files ?? []), file] }));

  const attachChartToLast = (chart: AiChatChart) =>
    updateLast(last => ({ ...last, charts: [...(last.charts ?? []), chart] }));

  const persist = async (conversationId: string) => {
    // Drop a trailing assistant message that never got content (e.g. stopped immediately).
    const finalMessages = messagesRef.current.filter(
      (m, i) =>
        !(
          i === messagesRef.current.length - 1 &&
          m.role === 'assistant' &&
          !m.content &&
          !m.files?.length &&
          !m.charts?.length
        ),
    );
    if (finalMessages.length !== messagesRef.current.length) commitMessages(finalMessages);
    if (!finalMessages.length) return;
    const existing = conversations.find(c => c.id === conversationId);
    const title = existing?.title || conversationTitle(finalMessages);
    try {
      const meta = await saveConversation(conversationId, title, finalMessages);
      setConversations(prev => [meta, ...prev.filter(c => c.id !== conversationId)]);
    } catch {
      setHistoryError(true);
    }
  };

  const addFiles = (files: FileList | File[]) => {
    Array.from(files).forEach(file => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const base: PendingAttachment = {
        id,
        name: file.name,
        size: file.size,
        mimetype: file.type || 'application/octet-stream',
        status: 'uploading',
      };
      if (file.size > AI_CHAT_MAX_UPLOAD_BYTES) {
        setPending(prev => [...prev, { ...base, status: 'error', error: t('tracking.ai_chat.attach_hint') }]);
        return;
      }
      setPending(prev => [...prev, base]);
      uploadAiChatFile(file)
        .then(attachment =>
          setPending(prev =>
            prev.map(p => (p.id === id ? { ...p, status: 'ready', attachment, mimetype: attachment.mimetype } : p)),
          ),
        )
        .catch((err: Error) =>
          setPending(prev => prev.map(p => (p.id === id ? { ...p, status: 'error', error: err.message } : p))),
        );
    });
  };

  const removePending = (id: string) => setPending(prev => prev.filter(p => p.id !== id));

  const uploading = pending.some(p => p.status === 'uploading');
  const readyAttachments = pending
    .filter(p => p.status === 'ready' && p.attachment)
    .map(p => p.attachment as AiChatAttachment);
  const canSend = !streaming && !uploading && (!!draft.trim() || readyAttachments.length > 0);

  const send = async (raw: string) => {
    const text = raw.trim();
    const attachments = readyAttachments;
    if ((!text && !attachments.length) || streaming || uploading) return;
    const conversationId = activeId ?? createConversationId();
    commitActiveId(conversationId);
    const userEntry: ChatEntry = {
      role: 'user',
      content: text,
      attachments: attachments.length ? attachments : undefined,
    };
    const history: ChatEntry[] = [...messages, userEntry];
    commitMessages([...history, { role: 'assistant', content: '' }]);
    setDraft('');
    setPending([]);
    setStreaming(true);
    setStatus(null);
    const controller = new AbortController();
    abortRef.current = controller;
    let stepCount = 0;
    try {
      await streamAiChat(
        history,
        {
          onText: delta => {
            setStatus(null);
            appendToLast(delta);
          },
          onToolUse: name => {
            stepCount += 1;
            setStatus({
              label: t(`tracking.ai_chat.activity.${name}`, { defaultValue: t('tracking.ai_chat.activity.default') }),
              step: stepCount,
            });
          },
          onFile: attachFileToLast,
          onChart: attachChartToLast,
          onError: msg => appendToLast(`\n\n⚠️ ${msg}`),
        },
        controller.signal,
      );
    } finally {
      setStreaming(false);
      setStatus(null);
      abortRef.current = null;
      // Skip persisting if the user switched conversations while this one was streaming.
      if (activeIdRef.current === conversationId) persist(conversationId);
    }
  };

  const stop = () => abortRef.current?.abort();

  const toggleHistory = () => {
    localStorage.setItem('aiChatHistoryOpen', historyOpen ? '0' : '1');
    setHistoryOpen(!historyOpen);
  };

  const newChat = () => {
    stop();
    commitMessages([]);
    commitActiveId(null);
    setDraft('');
    setPending([]);
  };

  const selectConversation = async (conversation: ConversationMeta) => {
    if (conversation.id === activeId) return;
    stop();
    commitActiveId(conversation.id);
    commitMessages([]);
    setPending([]);
    try {
      const loaded = await getConversationMessages<ChatEntry>(conversation.id);
      if (activeIdRef.current === conversation.id) commitMessages(loaded);
    } catch {
      setHistoryError(true);
    }
  };

  const removeConversation = async (conversation: ConversationMeta) => {
    try {
      await deleteConversation(conversation.id);
    } catch {
      setHistoryError(true);
      return;
    }
    setConversations(prev => prev.filter(c => c.id !== conversation.id));
    if (activeId === conversation.id) newChat();
  };

  const setFeedback = (index: number, value: 'up' | 'down' | null) => {
    const next = messagesRef.current.map((m, i) => (i === index ? { ...m, feedback: value ?? undefined } : m));
    commitMessages(next);
    if (activeId) sendMessageFeedback(activeId, index, value).catch(() => setHistoryError(true));
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragging(true);
  };

  // Hide only when the cursor really leaves the chat area: moving between the
  // chat's own children fires dragleave too, but then relatedTarget is inside
  // the root. An element that reports no relatedTarget (iframes, some overlays)
  // is judged by the pointer position instead.
  const onDragLeave = (e: React.DragEvent) => {
    if (!dragging) return;
    const root = rootRef.current;
    const next = e.relatedTarget as Node | null;
    if (next && root?.contains(next)) return;
    if (!next && root) {
      const under = document.elementFromPoint(e.clientX, e.clientY);
      if (under && root.contains(under)) return;
    }
    setDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragging(false);
    if (!streaming) addFiles(e.dataTransfer.files);
  };

  // Leaving the browser window (other screen, back to the file explorer) or
  // finishing the drag anywhere else ends the highlight as well.
  useEffect(() => {
    if (!dragging) return undefined;
    const hide = () => setDragging(false);
    const onWindowLeave = (e: DragEvent) => {
      if (e.relatedTarget !== null) return;
      const atEdge =
        e.clientX <= 1 || e.clientY <= 1 || e.clientX >= window.innerWidth - 1 || e.clientY >= window.innerHeight - 1;
      if (atEdge || !document.elementFromPoint(e.clientX, e.clientY)) hide();
    };
    window.addEventListener('dragleave', onWindowLeave);
    window.addEventListener('dragend', hide);
    window.addEventListener('drop', hide);
    return () => {
      window.removeEventListener('dragleave', onWindowLeave);
      window.removeEventListener('dragend', hide);
      window.removeEventListener('drop', hide);
    };
  }, [dragging]);

  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (!files.length || streaming) return;
    e.preventDefault();
    addFiles(files);
  };

  const groupLabel = (key: string) => {
    if (key === 'today') return t('tracking.ai_chat.today');
    if (key === 'yesterday') return t('tracking.ai_chat.yesterday');
    return new Date(key).toLocaleDateString(i18n.language);
  };

  const entryMeta = (conversation: ConversationMeta) => {
    const time = new Date(conversation.updatedAt).toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return conversation.hasFiles ? `${t('tracking.ai_chat.file_created')} · ${time}` : time;
  };

  const pendingMeta = (p: PendingAttachment): string => {
    if (p.status === 'uploading') return t('tracking.ai_chat.uploading');
    if (p.status === 'error') return p.error || t('tracking.ai_chat.upload_failed');
    return formatSize(p.size);
  };

  const lastIndex = messages.length - 1;

  return (
    <div
      ref={rootRef}
      className="relative flex h-full min-h-0 surface-base"
      onDragEnter={onDragEnter}
      onDragOver={e => {
        if (dragging) e.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}>
      {dragging && <div className={styles.dropOverlay}>{t('tracking.ai_chat.drop_here')}</div>}
      {!compact && historyOpen && (
        <aside className="hidden lg:flex w-[232px] shrink-0 flex-col surface-elevated border-subtle-right">
          <div className="flex flex-col gap-12 p-16 pb-12">
            <div className="flex items-center justify-between">
              <span className="typo-title-20 text-full">{t('tracking.ai_chat.title')}</span>
              <button
                type="button"
                onClick={toggleHistory}
                aria-label={t('tracking.ai_chat.collapse_history')}
                title={t('tracking.ai_chat.collapse_history')}
                className="flex items-center justify-center w-[28px] h-[28px] rounded-base border-none bg-transparent cursor-pointer text-faded hover:surface-elevated-darken">
                <Icon name="keyboard-tab-left" width={18} height={18} />
              </button>
            </div>
            <Button
              title={t('tracking.ai_chat.new_chat')}
              icon="plus"
              tone={ButtonToneProps.SECONDARY}
              onClick={newChat}
              className="w-full justify-center"
            />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 px-8 pb-8">
            {historyError && (
              <div className="px-8 py-4 typo-text-12 text-faded">{t('tracking.ai_chat.history_unavailable')}</div>
            )}
            {groups.map(group => (
              <React.Fragment key={group.key}>
                <div className="px-8 pt-8 pb-4 typo-text-12--bold text-faded">{groupLabel(group.key)}</div>
                {group.items.map(conversation => (
                  <div
                    key={conversation.id}
                    className={classNames(styles.historyEntry, conversation.id === activeId && styles.active)}>
                    <button
                      type="button"
                      onClick={() => selectConversation(conversation)}
                      className={styles.historyButton}>
                      <div
                        className={classNames(
                          'text-full truncate',
                          conversation.id === activeId ? 'typo-text-14--medium' : 'typo-text-14',
                        )}>
                        {conversation.title}
                      </div>
                      <div className="typo-text-12 text-faded mt-2">{entryMeta(conversation)}</div>
                    </button>
                    <button
                      type="button"
                      className={styles.historyDelete}
                      onClick={() => removeConversation(conversation)}
                      aria-label={t('tracking.ai_chat.delete_chat')}
                      title={t('tracking.ai_chat.delete_chat')}>
                      <Icon name="close" width={12} height={12} />
                    </button>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </aside>
      )}

      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        {!compact && (
          <div className="flex items-center justify-between gap-16 px-32 py-14 surface-elevated border-subtle-bottom">
            {!historyOpen && (
              <button
                type="button"
                onClick={toggleHistory}
                aria-label={t('tracking.ai_chat.expand_history')}
                title={t('tracking.ai_chat.expand_history')}
                className="hidden lg:flex items-center justify-center w-[28px] h-[28px] shrink-0 rounded-base border-none bg-transparent cursor-pointer text-faded hover:surface-elevated-darken">
                <Icon name="keyboard-tab-right" width={18} height={18} />
              </button>
            )}
            <span className="typo-title-20 text-full lg:hidden">{t('tracking.ai_chat.title')}</span>
            <span className="typo-text-12 text-faded ml-auto">{t('tracking.ai_chat.connected_hint')}</span>
          </div>
        )}

        <div
          ref={scrollRef}
          aria-live="polite"
          className={classNames('flex-1 min-h-0 overflow-y-auto flex flex-col', compact && styles.msgsCompact)}>
          {messages.length === 0 && compact ? (
            <div className={styles.msgList}>
              <div className={classNames(styles.bubble, styles.bubbleAssistant)}>{t('ai_widget.intro')}</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="m-auto flex flex-col items-center gap-20 text-center max-w-[620px] px-24 py-24">
              <div className="w-[44px] h-[44px] rounded-[10px] surface-primary-gradient flex items-center justify-center text-white">
                <Icon name="ai-chat" width={22} height={22} />
              </div>
              <div className="flex flex-col gap-8">
                <div className="typo-title-24 text-full">{t('tracking.ai_chat.empty_title')}</div>
                <div className="typo-text-14 text-faded">{t('tracking.ai_chat.empty_subtitle')}</div>
              </div>
              <div className="flex flex-wrap gap-8 justify-center">
                {SUGGESTION_KEYS.map(key => (
                  <button
                    key={key}
                    type="button"
                    className={styles.chip}
                    onClick={() => send(t(`tracking.ai_chat.${key}`))}>
                    {t(`tracking.ai_chat.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className={classNames(styles.msgList, !compact && 'mx-auto max-w-[1020px]')}>
              {messages.map((m, i) => {
                const isStreamingTail = streaming && i === lastIndex && m.role === 'assistant';
                const hasBody = !!m.content || !!m.files?.length || !!m.charts?.length;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={i}>
                    {m.role === 'user' ? (
                      <div className={classNames(styles.bubble, styles.bubbleUser)}>
                        {!!m.attachments?.length && (
                          <div className="flex flex-wrap gap-6">
                            {m.attachments.map(a => (
                              <AttachmentChip
                                key={a.attachmentId}
                                name={a.filename}
                                mimetype={a.mimetype}
                                meta={a.pages ? `${a.pages} p.` : formatSize(a.size)}
                              />
                            ))}
                          </div>
                        )}
                        {m.content && <div className={styles.bubbleText}>{m.content}</div>}
                      </div>
                    ) : (
                      <>
                        {hasBody && (
                          <div
                            className={classNames(
                              styles.assistantRow,
                              (m.charts?.length || m.files?.length) && styles.assistantRowWide,
                            )}>
                            {!compact && <AssistantAvatar />}
                            <div
                              className={classNames(
                                styles.bubble,
                                styles.bubbleAssistant,
                                (m.charts?.length || m.files?.length) && styles.bubbleWide,
                              )}>
                              {m.content && <Markdown content={m.content} />}
                              {m.charts?.map((chart, ci) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <ChartCard key={ci} chart={chart} />
                              ))}
                              {m.files?.map(file => <FileCard key={file.attachmentId} file={file} />)}
                              {!isStreamingTail && activeId && (
                                <FeedbackButtons value={m.feedback} onChange={value => setFeedback(i, value)} />
                              )}
                            </div>
                          </div>
                        )}
                        {isStreamingTail &&
                          (!hasBody ? (
                            <ThinkingIndicator
                              label={status?.label ?? t('tracking.ai_chat.activity.default')}
                              step={status?.step}
                              compact={compact}
                            />
                          ) : (
                            status && (
                              <ThinkingIndicator
                                label={status.label}
                                step={status.step}
                                withAvatar={false}
                                compact={compact}
                              />
                            )
                          ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {compact && messages.length === 0 && (
          <div className={styles.chipsRow}>
            {SUGGESTION_KEYS.map(key => (
              <button
                key={key}
                type="button"
                className={styles.chip}
                onClick={() => send(t(`tracking.ai_chat.${key}`))}>
                {t(`tracking.ai_chat.${key}`)}
              </button>
            ))}
          </div>
        )}
        <div className={classNames('border-subtle-top surface-elevated', compact ? 'px-12 py-12' : 'px-24 py-16')}>
          <div className="mx-auto w-full max-w-[1020px] flex flex-col gap-8">
            <div className={styles.composer}>
              {pending.length > 0 && (
                <div className="flex flex-wrap gap-6 pt-6">
                  {pending.map(p => (
                    <AttachmentChip
                      key={p.id}
                      name={p.name}
                      mimetype={p.mimetype}
                      meta={pendingMeta(p)}
                      error={p.status === 'error'}
                      onRemove={() => removePending(p.id)}
                    />
                  ))}
                </div>
              )}
              <div className="flex items-end gap-8">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={AI_CHAT_ACCEPT}
                  multiple
                  hidden
                  onChange={e => {
                    if (e.target.files) addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={streaming}
                  aria-label={t('tracking.ai_chat.attach')}
                  title={`${t('tracking.ai_chat.attach')} · ${t('tracking.ai_chat.attach_hint')}`}
                  className="flex items-center justify-center w-[36px] h-[36px] shrink-0 rounded-base border-none bg-transparent cursor-pointer text-faded hover:surface-elevated-darken disabled:cursor-default">
                  <Icon name="attachFile" width={18} height={18} />
                </button>
                <textarea
                  ref={textareaRef}
                  className={styles.textarea}
                  rows={1}
                  value={draft}
                  placeholder={compact ? t('ai_widget.placeholder') : t('tracking.ai_chat.placeholder')}
                  onChange={e => setDraft(e.target.value)}
                  onPaste={onPaste}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send(draft);
                    }
                  }}
                />
                {streaming ? (
                  <button
                    type="button"
                    onClick={stop}
                    className="flex items-center gap-8 h-[36px] px-14 shrink-0 rounded-base border-distinct surface-elevated typo-text-14--medium text-full cursor-pointer hover:surface-elevated-darken">
                    <span className="w-[10px] h-[10px] rounded-[2px] status-error-full-bg" />
                    {t('tracking.ai_chat.stop')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => send(draft)}
                    disabled={!canSend}
                    aria-label={t('tracking.ai_chat.send')}
                    className={classNames(styles.send, !canSend && styles.sendDisabled)}>
                    <Icon name="arrowRight" width={16} height={16} />
                  </button>
                )}
              </div>
            </div>
            <div className={styles.foot}>
              {streaming ? t('tracking.ai_chat.disclaimer_working') : t('tracking.ai_chat.disclaimer_idle')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiChat;
